import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { useAgentStore } from "../agent/agentStore";
import { runQuery } from "../api/endpoints";
import type { MeView } from "../api/types";
import { locate } from "../app/screenRoutes";
import { LiveClient } from "./liveClient";
import { getHeartbeatLocation, useLocationStore } from "./location";
import { createLiveSink, dropWorkspaceQueries } from "./sink";
import { useLiveStore } from "./store";

let current: LiveClient | null = null;

/** The running live client, for the few things a screen sends over the socket (still editing). */
export function getLiveClient(): LiveClient | null {
  return current;
}

/**
 * Keeps one socket open for the active space (BUILD-PLAN 2.11.4). A person without a discipline
 * (the admin) has no socket. Switching discipline closes the socket, drops what was read in the old
 * one and joins the new room.
 */
export function LiveProvider({ me, children }: { me: MeView; children: ReactNode }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const membershipId = me.active?.membership_id ?? null;
  const space = me.active?.space ?? null;
  const spaceKey = membershipId ? `${membershipId}:${space}` : null;
  const previousSpace = useRef<string | null>(null);

  // The address is where the person is, until a screen reports better.
  useEffect(() => {
    const here = locate(location.pathname);
    useLocationStore.getState().setRoute({ screen: here.screen, itemId: here.itemId, sessionId: here.sessionId });
    current?.sendHeartbeat();
  }, [location.pathname]);

  // Another discipline: what was read in the old one is not shown in the new one.
  useEffect(() => {
    if (previousSpace.current !== null && previousSpace.current !== spaceKey) {
      dropWorkspaceQueries(queryClient);
      useAgentStore.getState().reset();
    }
    previousSpace.current = spaceKey;
  }, [spaceKey, queryClient]);

  useEffect(() => {
    if (!spaceKey) return;
    const client = new LiveClient({
      sink: createLiveSink(queryClient),
      membershipId,
      fetchEventsAfter: (after, limit) => runQuery("events_after", { after, limit }),
      getLocation: getHeartbeatLocation,
    });
    current = client;
    client.start();
    const online = () => client.reconnectNow();
    window.addEventListener("online", online);
    return () => {
      window.removeEventListener("online", online);
      client.stop();
      if (current === client) current = null;
      useLiveStore.getState().reset();
    };
  }, [spaceKey, membershipId, queryClient]);

  return <>{children}</>;
}
